/**
 * Fixed Route operating-cost calculator (pure).
 *
 * Computes the daily and annual operating cost of a fixed route from three rates
 * (daily fixed cost per vehicle, cost per km, hourly wage) and the quantities already
 * carried on the route (distance, trips/day, vehicles, service-hours, service days).
 *
 * This is a two-and-a-half-variable reduction of the classic transit three-variable
 * fully-allocated cost model `Cost = c_H·hours + c_K·km + c_V·vehicles`:
 *  - `hourlyWage`  → labour term (`c_H`), charged per vehicle service-hour;
 *  - `costPerKm`   → distance term (`c_K`);
 *  - `dailyFixedCostPerVehicle` → the per-vehicle capital/overhead term (`c_V`),
 *    charged once per vehicle per day of operation.
 *
 * Deliberate v1 approximations (see the PRD's Non-Goals): peak-vehicle derivation for
 * trip-based routes (we use `vehicleCount ?? 1`), deadhead, layover, and the
 * platform-vs-revenue-hour distinction. The trips/day + distance derivation mirrors the
 * existing capacity heuristic (`FixedRoutesStatistics`/`RouteBuilder`) so cost and capacity agree.
 *
 * Mirrors the structure of `fleetOptimisationCalculator.ts` (typed inputs, form
 * parse/validate, pure `compute`).
 */

import type {
  FixedRoute,
  CostRates,
  NetworkCostSettings,
  ServiceDaysMask,
} from '../../types/fixedRoutes';

/** Standard annualisation basis: operating days per week × this. */
export const WEEKS_PER_YEAR = 52;

const SERVICE_DAY_KEYS: ReadonlyArray<keyof ServiceDaysMask> = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

/** Default operating days/week when a route has no service calendar (weekday service). */
const DEFAULT_OPERATING_DAYS_PER_WEEK = 5;

/** Quantities derived from a route, used to drive the cost formula. */
export interface RouteCostQuantities {
  /** One-way route distance in km (return segment excluded unless circulator). */
  distanceKm: number;
  /** Number of vehicles assigned (`vehicleCount`, min 1). */
  vehicles: number;
  /** Vehicle trips per operating day. */
  tripsPerDay: number;
  /** Total vehicle-km per operating day (`tripsPerDay × distanceKm`). */
  dailyKm: number;
  /** Total vehicle service-hours per operating day (labour basis). */
  serviceHoursPerDay: number;
  /** Days per week the route operates (from the service-days mask). */
  operatingDaysPerWeek: number;
}

/** Cost result for a single route. */
export interface RouteCostResult {
  routeId: string;
  routeName: string;
  /** Cost for one day the route operates. */
  dailyCost: number;
  /** `dailyCost × operatingDaysPerWeek × 52`. */
  annualCost: number;
  /** Component: `vehicles × dailyFixedCostPerVehicle`. */
  fixedCost: number;
  /** Component: `dailyKm × costPerKm`. */
  distanceCost: number;
  /** Component: `serviceHoursPerDay × hourlyWage`. */
  labourCost: number;
  /** `operatingDaysPerWeek × 52`. */
  operatingDaysPerYear: number;
  /** The effective rates applied (network default merged with any per-route override). */
  rates: CostRates;
  /** True when a per-route override changed at least one rate. */
  hasOverride: boolean;
  /** The derived quantities (for display/debugging). */
  quantities: RouteCostQuantities;
}

/** Network-wide cost rollup. */
export interface NetworkCostResult {
  perRoute: RouteCostResult[];
  totalDailyCost: number;
  totalAnnualCost: number;
  totalFixedCost: number;
  totalDistanceCost: number;
  totalLabourCost: number;
}

// ─── Quantity derivation ─────────────────────────────────────────────────────

function parseTimeToMinutes(timeStr?: string): number | null {
  if (!timeStr) return null;
  const parts = timeStr.split(':');
  if (parts.length < 2) return null;
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
  return hours * 60 + minutes;
}

/** Operating span in minutes from start/end times (handles overnight wrap). Null if unknown. */
function operatingMinutes(route: Pick<FixedRoute, 'startTime' | 'endTime'>): number | null {
  const start = parseTimeToMinutes(route.startTime);
  const end = parseTimeToMinutes(route.endTime);
  if (start === null || end === null) return null;
  return end >= start ? end - start : end + 24 * 60 - start;
}

/**
 * Sum of segment distances in metres. The return segment (last stop → first stop) is
 * excluded for non-circulators, matching the distance shown on the route card.
 */
function routeDistanceMeters(route: Pick<FixedRoute, 'segments' | 'stops' | 'isCirculator'>): number {
  const isCirculator = route.isCirculator || false;
  const firstStopId = route.stops[0]?.stopId;
  const lastStopId = route.stops[route.stops.length - 1]?.stopId;
  return route.segments.reduce((sum, seg) => {
    const isReturn = seg.fromStopId === lastStopId && seg.toStopId === firstStopId;
    if (!isReturn || isCirculator) {
      return sum + (seg.distanceMeters || 0);
    }
    return sum;
  }, 0);
}

/** Loop/route duration in minutes: travel time of all segments + stop wait times. */
function loopDurationMinutes(route: Pick<FixedRoute, 'segments' | 'stops'>): number {
  const travel = route.segments.reduce((s, seg) => s + (seg.travelTimeMinutes || 0), 0);
  const wait = route.stops.reduce((s, stop) => s + (stop.waitTimeMinutes || 0), 0);
  return travel + wait;
}

/** Days per week the route operates. Defaults to weekday (5) when no mask is present. */
export function operatingDaysPerWeek(mask: ServiceDaysMask | undefined): number {
  if (!mask) return DEFAULT_OPERATING_DAYS_PER_WEEK;
  return SERVICE_DAY_KEYS.reduce((n, key) => (mask[key] ? n + 1 : n), 0);
}

/**
 * Derive the cost-driving quantities from a route. Trips/day mirrors the capacity heuristic:
 *  - circulator → `(span / loopDuration) × vehicleCount`
 *  - trip-based → `tripSchedules.length`
 *  - otherwise  → `vehicles` (each vehicle runs the route once)
 */
export function deriveRouteCostQuantities(route: FixedRoute): RouteCostQuantities {
  const isCirculator = route.isCirculator || false;
  const distanceKm = routeDistanceMeters(route) / 1000;
  const vehicles = route.vehicleCount && route.vehicleCount >= 1 ? route.vehicleCount : 1;
  const opMins = operatingMinutes(route);
  const loopMins = loopDurationMinutes(route);

  let tripsPerDay = 0;
  let serviceHoursPerDay = 0;

  if (isCirculator && route.vehicleCount && route.vehicleCount >= 1 && loopMins > 0 && opMins && opMins > 0) {
    // Each of `vehicleCount` vehicles loops continuously across the operating span.
    tripsPerDay = (opMins / loopMins) * route.vehicleCount;
    serviceHoursPerDay = (opMins / 60) * route.vehicleCount;
  } else if (route.tripSchedules && route.tripSchedules.length > 0) {
    // One vehicle-run per scheduled trip; labour = sum of trip durations (revenue hours).
    tripsPerDay = route.tripSchedules.length;
    serviceHoursPerDay =
      route.tripSchedules.reduce((sum, trip) => {
        const arrivals = trip.stopArrivalTimesMinutes || [];
        const duration = arrivals.length > 0 ? arrivals[arrivals.length - 1] : 0;
        return sum + (Number.isFinite(duration) && duration > 0 ? duration : 0);
      }, 0) / 60;
  } else {
    // Fallback: each vehicle runs the route once over the operating span.
    tripsPerDay = vehicles;
    const spanHours = opMins && opMins > 0 ? opMins / 60 : loopMins > 0 ? loopMins / 60 : 0;
    serviceHoursPerDay = spanHours * vehicles;
  }

  return {
    distanceKm,
    vehicles,
    tripsPerDay,
    dailyKm: tripsPerDay * distanceKm,
    serviceHoursPerDay,
    operatingDaysPerWeek: operatingDaysPerWeek(route.serviceDays),
  };
}

// ─── Cost computation ────────────────────────────────────────────────────────

/**
 * Effective rates for a route: the network default with any per-route override applied.
 * A `0` override is respected (only `null`/`undefined` inherit the default).
 */
export function effectiveRates(settings: NetworkCostSettings, route: Pick<FixedRoute, 'costOverride'>): CostRates {
  const o = route.costOverride;
  return {
    dailyFixedCostPerVehicle: o?.dailyFixedCostPerVehicle ?? settings.dailyFixedCostPerVehicle,
    costPerKm: o?.costPerKm ?? settings.costPerKm,
    hourlyWage: o?.hourlyWage ?? settings.hourlyWage,
  };
}

/** Whether a route carries at least one non-null cost override. */
export function hasCostOverride(route: Pick<FixedRoute, 'costOverride'>): boolean {
  const o = route.costOverride;
  if (!o) return false;
  return (
    o.dailyFixedCostPerVehicle != null ||
    o.costPerKm != null ||
    o.hourlyWage != null
  );
}

/** Compute the daily + annual operating cost of a single route. */
export function computeRouteCost(route: FixedRoute, settings: NetworkCostSettings): RouteCostResult {
  const quantities = deriveRouteCostQuantities(route);
  const rates = effectiveRates(settings, route);

  const fixedCost = quantities.vehicles * rates.dailyFixedCostPerVehicle;
  const distanceCost = quantities.dailyKm * rates.costPerKm;
  const labourCost = quantities.serviceHoursPerDay * rates.hourlyWage;
  const dailyCost = fixedCost + distanceCost + labourCost;

  const operatingDaysPerYear = quantities.operatingDaysPerWeek * WEEKS_PER_YEAR;
  const annualCost = dailyCost * operatingDaysPerYear;

  return {
    routeId: route.id,
    routeName: route.name,
    dailyCost,
    annualCost,
    fixedCost,
    distanceCost,
    labourCost,
    operatingDaysPerYear,
    rates,
    hasOverride: hasCostOverride(route),
    quantities,
  };
}

/** Compute per-route costs and the network rollup. */
export function computeNetworkCost(routes: FixedRoute[], settings: NetworkCostSettings): NetworkCostResult {
  const perRoute = routes.map((route) => computeRouteCost(route, settings));

  const totals = perRoute.reduce(
    (acc, r) => {
      acc.totalDailyCost += r.dailyCost;
      acc.totalAnnualCost += r.annualCost;
      acc.totalFixedCost += r.fixedCost;
      acc.totalDistanceCost += r.distanceCost;
      acc.totalLabourCost += r.labourCost;
      return acc;
    },
    {
      totalDailyCost: 0,
      totalAnnualCost: 0,
      totalFixedCost: 0,
      totalDistanceCost: 0,
      totalLabourCost: 0,
    },
  );

  return { perRoute, ...totals };
}

// ─── Form parsing / validation (for the rate editor) ───────────────────────────

export type CostRateFieldKey = keyof CostRates;

export interface CostSettingsFormValues {
  currency: string;
  dailyFixedCostPerVehicle: string;
  costPerKm: string;
  hourlyWage: string;
}

export type CostSettingsValidationErrors = Partial<Record<CostRateFieldKey, string>>;

const REQUIRED_ERROR = 'Enter a valid non-negative number';

/** Parse a string to a finite non-negative number, or null if invalid/blank. */
export function parseNonNegativeNumber(value: string): number | null {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

/** Convert validated network settings to editable string form values. */
export function costSettingsToFormValues(settings: NetworkCostSettings): CostSettingsFormValues {
  return {
    currency: settings.currency,
    dailyFixedCostPerVehicle: String(settings.dailyFixedCostPerVehicle),
    costPerKm: String(settings.costPerKm),
    hourlyWage: String(settings.hourlyWage),
  };
}

/** Parse + validate the rate-editor form into `NetworkCostSettings`. */
export function parseCostSettings(values: CostSettingsFormValues): {
  isValid: boolean;
  settings: NetworkCostSettings | null;
  errors: CostSettingsValidationErrors;
} {
  const errors: CostSettingsValidationErrors = {};
  const numericFields: CostRateFieldKey[] = ['dailyFixedCostPerVehicle', 'costPerKm', 'hourlyWage'];
  const parsed: Partial<CostRates> = {};

  numericFields.forEach((field) => {
    const value = parseNonNegativeNumber(values[field]);
    if (value === null) {
      errors[field] = REQUIRED_ERROR;
    } else {
      parsed[field] = value;
    }
  });

  const currency = values.currency.trim() || 'USD';
  const isValid = Object.keys(errors).length === 0;

  return {
    isValid,
    settings: isValid
      ? {
          currency,
          dailyFixedCostPerVehicle: parsed.dailyFixedCostPerVehicle as number,
          costPerKm: parsed.costPerKm as number,
          hourlyWage: parsed.hourlyWage as number,
        }
      : null,
    errors,
  };
}
